'use client';

import { useEffect } from 'react';
import { getYMMCookies, setYMMCookies } from '@/packages/sxm/ymm';
import { resolveVehicleFromQueryString, YMM_QUERY_PARAMS } from '@/packages/sxm/ymm/ymmQueryString';
import { reloadPage, revealPage } from '@/packages/sxm/ymm/ymmQueryStringGuard';
import { determinePlatformFromCapabilities } from './useVehiclePlatformSync';

/**
 * Preselects the vehicle from `?year=&make=&model=` (e.g. CRM email links). A valid combination
 * overrides any existing YMM cookie and is saved the same way the YMM modal saves it. Invalid or
 * incomplete params are ignored and the existing cookie/modal behavior is untouched.
 *
 * The layout's inline guard script blanks the page while these params are present; every path here
 * reveals it again except the reload, whose fresh page no longer has the params.
 */
export function useYmmQueryString() {
    useEffect(() => {
        const { search } = window.location;
        const params = new URLSearchParams(search);
        if (!YMM_QUERY_PARAMS.every((name) => params.has(name))) {
            return;
        }

        let cancelled = false;

        void resolveVehicleFromQueryString(search)
            .then((vehicle) => {
                if (cancelled) {
                    return;
                }
                if (!vehicle) {
                    revealPage();
                    return;
                }

                const platform = determinePlatformFromCapabilities(vehicle.capabilities);
                // getYMMCookies() already defaults an unset platform to the same value the server uses.
                const platformChanged = getYMMCookies().sxm_platform !== platform;

                setYMMCookies({
                    sxm_radio: `YMM|${vehicle.year}|${vehicle.make}|${vehicle.model}|${vehicle.id}`,
                    sxm_platform: platform,
                    inft_ymm: vehicle.capabilities.infotainment.join('|'),
                    ymm_bar: '',
                    roadblockbypass: '',
                });

                // Drop only the YMM params so the reload doesn't re-run this and loop.
                const url = new URL(window.location.href);
                YMM_QUERY_PARAMS.forEach((name) => url.searchParams.delete(name));
                window.history.replaceState(window.history.state, '', url);

                // Only `sxm_platform` changes the server-rendered page variant (it is in Akamai's cache
                // key), so reload just for that. `inft_ymm` is not in the key, so a reload would not
                // fetch different infotainment content anyway.
                if (platformChanged) {
                    reloadPage();
                    return;
                }
                revealPage();
            })
            .catch((error) => {
                console.error('Unable to apply YMM query string parameters', error);
                revealPage();
            });

        return () => {
            cancelled = true;
            revealPage();
        };
    }, []);
}
